import { labelStyles } from 'components/MainComponent/LabelStyles'
import { groups } from 'd3-array'
import { useLabels } from 'Hooks/useLabels'
import styled from 'styled-components'
import { useWindowSize } from 'usehooks-ts'

import importData from './kaffeetabelle.json'

let tableData: tableDataType[] = importData as tableDataType[]
type tableDataType = {
  group: number
  ismain: number
  drink: string
  drink_fr: string
  per100: number
  portion: number
  caffeine: number
  isaverage: number
}
export const DrinkTable = ({ lang }: { lang: 'de' | 'fr' | undefined }) => {
  let { width } = useWindowSize()
  const Labels = useLabels(labelStyles)
  const drinksAtEndOrder = [
    'Flat White',
    'Grüntee',
    'Coca Cola',
    'Schwarztee',
    'Matcha',
    'Red Bull',
    'Cold Brew Coffee (8h)',
  ]
  const drinksAtEndOrderMap = new Map<string, number>(
    drinksAtEndOrder.map((drink, index) => [drink, index])
  )
  let maxValue = tableData
    .map((d) => d.caffeine)
    .reduce((a, b) => Math.max(a, b))

  function percent(value: number) {
    return (value / maxValue) * 100 + '%'
  }

  let groupedByCat: [number, tableDataType[]][] = groups(
    tableData,
    (d) => d.group
  ).sort(
    (a, b) =>
      a[1][0].ismain - b[1][0].ismain || a[1][0].caffeine - b[1][0].caffeine
  )
  const sortedTableData = groupedByCat.flatMap(([, groupItems]) => groupItems)
  const drinksAtEnd = sortedTableData
    .filter((item) => drinksAtEndOrderMap.has(item.drink))
    .sort(
      (a, b) =>
        (drinksAtEndOrderMap.get(a.drink) ?? 0) -
        (drinksAtEndOrderMap.get(b.drink) ?? 0)
    )
  const sortedTableDataWithCustomOrder = [
    ...sortedTableData.filter((item) => !drinksAtEndOrderMap.has(item.drink)),
    ...drinksAtEnd,
  ]

  return (
    <DrinkTableRoot>
      <Labels chartTitle>
        {lang === 'de' ? (
          <>Koffeingehalt (in Milligramm) nach Getränk und Zubereitungsart</>
        ) : (
          <>
            Teneur en caféine (en milligramme) selon la boisson et le mode de
            préparation
          </>
        )}
      </Labels>
      <Table>
        <Head>
          <tr>
            <td className='drink'></td>
            <td>
              <b>{lang === 'de' ? 'Koffein' : 'Caféine'}</b>
              <br />
              {lang === 'de' ? 'pro' : 'pour'} 100 ml
            </td>
            <td className='num'>
              <b>Portion</b>
              <br />
              {lang === 'de' ? 'in' : 'par'} ml
            </td>
            <td className='num'>
              <b>{lang === 'de' ? 'Koffein' : 'Caféine'}</b>
              <br />
              {lang === 'de' ? 'pro Portion' : 'par portion'}
            </td>
            <td className='ta pa bar'></td>
          </tr>
        </Head>
        <Body>
          {sortedTableDataWithCustomOrder.map((element) => {
            const { drink, drink_fr, ismain, portion, per100, caffeine } =
              element as tableDataType

            const unimportant = ismain === 0
            const important = ismain === 1
            return (
              <tr
                className={
                  important ? 'main' : unimportant ? 'tertiary' : 'secondary'
                }
              >
                <td className={'la drink ' + (unimportant ? 'pa' : '')}>
                  {lang === 'de' ? drink : drink_fr}
                </td>
                <td className='num'>{per100}</td>
                <td className='num'>{portion}</td>
                <td className='num'> {caffeine}</td>
                <td className='ta pa bar' style={{ textAlign: 'left' }}>
                  <Bar
                    style={{
                      width: percent(caffeine),
                      backgroundColor: important
                        ? ' var(--table-bar-important)'
                        : ' var(--table-bar-unimportant)',
                    }}
                  />
                </td>
              </tr>
            )
          })}

          <tr>
            <td colSpan={4} className='footnote'>
              {lang === 'de' ? (
                <>
                  Hinweise: KVA = Kaffeevollautomat, Sieb = Siebträgermaschine,
                  Cappuccino und Latte Macchiato enthalten einen Espresso, beim
                  Kapsel-Kaffee handelt es sich um einen Durchschnittswert,
                  Quelle:{' '}
                  <a href="https://journals.sagepub.com/doi/full/10.1177/0260106018810941#:~:text=Caffeine%20content%20(all%20pods)%20ranged,%E2%80%93162%25%20of%20manufacturer's%20values.">
                    Griffith University
                  </a>
                </>
              ) : (
                <>
                  Remarques: MA = machine à café automatique, PE = machine à
                  percolateur, Cappuccino et Latte Macchiato contiennent un
                  expresso, pour le café en capsule, il s’agit d’une valeur
                  moyenne, source:{' '}
                  <a href="https://journals.sagepub.com/doi/full/10.1177/0260106018810941#:~:text=Caffeine%20content%20(all%20pods)%20ranged,%E2%80%93162%25%20of%20manufacturer's%20values.">
                    Griffith University
                  </a>
                </>
              )}
            </td>
          </tr>
        </Body>
      </Table>
    </DrinkTableRoot>
  )
}

const DrinkTableRoot = styled.div`
  font-family: var(--font-sans-serif);
  margin-top: 30px;
  margin-bottom: 60px;
  @media screen and (max-width: 711px) {
    width: calc(100vw - 40px);
  }
`

const Bar = styled.div`
  background-color: var(--coffee-dark);
  display: inline-block;
  height: 14px;
`
const Table = styled.table`
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 14px;
  width: 100%;
  line-height: 30px;

  td {
    /* white-space: nowrap; */
    @media screen and (max-width: 711px) {
      white-space: pre-wrap;
    }
    border-bottom: 1px solid var(--table-line-borders);
    /* color: var(--coffee-color); */
    &.bar {
      padding-top: 8px;
      @media screen and (max-width: 711px) {
        display: none;
      }
    }
    &.footnote {
      white-space: pre-wrap;
      border: 0;
      padding: 0;
      line-height: 1.5em;
      font-size: 12px;
      text-align: left;
      padding-top: 15px;
      color: var(--table-unimportant-row);
      a {
        background: none;
      }
    }

    &.la {
      /* text-align: left; */
    }
    &.pa {
      padding-left: 15px;
      @media screen and (max-width: 711px) {
        padding-left: 8px;
      }
    }
    &.sm {
      font-size: 12px;
    }
    &.drink {
      width: 30%;
      @media screen and (max-width: 711px) {
        width: 46%;
      }
    }
    &.num {
      width: 15%;
      @media screen and (max-width: 711px) {
        width: 18%;
      }
    }
    &.bar {
      width: 20%;
      @media screen and (max-width: 711px) {
        display: none;
      }
    }
  }
  tr {
    &.main {
      td {
        font-weight: 700;
        @media screen and (max-width: 711px) {
          line-height: 1.4em;
        }
      }
    }
    &.tertiary {
      td {
        line-height: 1.3em;
        border: 0;
        color: var(--table-unimportant-row);
      }
    }
  }
`
const Head = styled.thead`
  font-size: 15px;
  color: var(--table-unimportant-row);
  line-height: 1.3em;
  vertical-align: baseline;
  @media screen and (max-width: 711px) {
    font-size: 13px;
  }
  tr {
    text-align: left !important;
    td {
      text-align: right;
      border: 0;
      &.la {
        text-align: left !important;
      }
      &.pa {
        padding-left: 15px;
        @media screen and (max-width: 711px) {
          padding-left: 8px;
        }
      }
      &.smw {
        width: 60px;
        @media screen and (max-width: 711px) {
          width: 40px;
        }
      }
    }
  }
`
const Body = styled.tbody`
  tr {
    td {
      text-align: right;
      &.la {
        text-align: left !important;
      }
      &.pa {
        padding-left: 15px;
        @media screen and (max-width: 711px) {
          padding-left: 8px;
        }
      }
    }
  }
`
